import React from 'react';
import {
  HiddenFileInput,
  BulkUploadDropArea,
  BulkUploadAddFilesButton,
  BulkUploadDropHint,
  BulkUploadAddMoreZone,
} from '../../styles';

export const BULK_UPLOAD_ACCEPT = '.pdf,.docx,.jpeg,.jpg,.png,.heic,.heif';

export const BULK_UPLOAD_EMPTY_HINT =
  '(Files should be in .pdf, .docx, .jpeg, .png or .heic/.heif format. Maximum size: PDF 100 MB, image 32 MB, Word 25 MB)';

export const BULK_UPLOAD_MORE_HINT =
  '(Maximum size: PDF 100 MB, image 32 MB, Word 25 MB)';

const stop = (event) => {
  event.preventDefault();
  event.stopPropagation();
};

/**
 * The bulk upload drop zone (build doc 06 §7.3), shared by the v1 Add
 * responses step and the v2 intake: the hidden file input, the "Select Files"
 * / "Add More Files" button with its hints, and drag-and-drop onto the zone.
 *
 * `fileSection` renders under "Add More Files" once there are files (the
 * caller's file list); `failedSection` renders in both states, so a drop where
 * every file failed still shows what went wrong.
 */
export const FileDropArea = ({
  inputRef,
  hasFiles,
  isDragging,
  onDraggingChange,
  onFiles,
  disabled = false,
  isProcessing = false,
  processingHint = null,
  accept = BULK_UPLOAD_ACCEPT,
  emptyHint = BULK_UPLOAD_EMPTY_HINT,
  moreHint = BULK_UPLOAD_MORE_HINT,
  fileSection = null,
  failedSection = null,
}) => (
  <>
    <HiddenFileInput
      ref={inputRef}
      type="file"
      disabled={disabled}
      multiple
      accept={accept}
      data-testid="bulk-upload-file-input"
      onChange={(e) => {
        onFiles(e.target.files);
        e.target.value = '';
      }}
    />
    <BulkUploadDropArea
      $hasFiles={hasFiles}
      data-active={isDragging}
      data-testid="bulk-upload-drop-area"
      data-has-files={hasFiles}
      onDragOver={(e) => {
        stop(e);
        if (!disabled) onDraggingChange(true);
      }}
      onDragEnter={stop}
      onDragLeave={(e) => {
        stop(e);
        onDraggingChange(false);
      }}
      onDrop={(e) => {
        stop(e);
        onDraggingChange(false);
        if (!disabled) onFiles(e.dataTransfer.files);
      }}
    >
      {!hasFiles ? (
        <>
          <BulkUploadAddFilesButton
            type="button"
            data-testid="bulk-upload-add-files-button"
            onClick={() => inputRef.current?.click()}
            disabled={disabled || isProcessing}
          >
            {isProcessing ? 'Processing...' : 'Select Files'}
          </BulkUploadAddFilesButton>
          <BulkUploadDropHint>Or drop files here</BulkUploadDropHint>
          <BulkUploadDropHint data-size="sm">{emptyHint}</BulkUploadDropHint>
          {isProcessing && processingHint && (
            <BulkUploadDropHint style={{ color: '#7200e0', fontWeight: 500 }}>
              {processingHint}
            </BulkUploadDropHint>
          )}
        </>
      ) : (
        <>
          <BulkUploadAddMoreZone>
            <BulkUploadAddFilesButton
              type="button"
              $outline
              onClick={() => inputRef.current?.click()}
              disabled={disabled || isProcessing}
            >
              {isProcessing ? 'Processing...' : 'Add More Files'}
            </BulkUploadAddFilesButton>
            <BulkUploadDropHint>Or drop files here</BulkUploadDropHint>
            <BulkUploadDropHint data-size="sm">{moreHint}</BulkUploadDropHint>
          </BulkUploadAddMoreZone>
          {fileSection}
        </>
      )}
      {failedSection}
    </BulkUploadDropArea>
  </>
);

export default FileDropArea;
